import { useCallback, useContext, useEffect, useState } from 'react';

import { Form as FormAntd, Radio } from 'antd';
import { Tabs } from '@hdi-seguros/web-components.tabs';

import {
  ColSize,
  ConfirmationModal,
  ContentSection,
  Form,
  RowSize,
} from '../../../../../../../../../shared/components';
import {
  GenericDataFormContext,
  ModalDeleteTab,
  PLAN_TYPE,
  QuotationInfoContext,
  QuotationStep,
  QuotationStepButtons
} from '../../../../../shared';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import { StepTwoFormContext } from '../../contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';
import { StepThreeFormContext } from '../../../StepThree/contexts/StepThreeForm';

import { FeatureRisk } from '../../../../../../../../../shared/interfaces';

import './index.css';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';

export const PropertyDataHome = () => {
  const {
    activeKey,
    addNewTab,
    allFeatures,
    areTabActionsAllowed,
    homeFormValues,
    isPlanTypeADisabled,
    isPlanTypeCDisabled,
    keyTabToDelete,
    planTypeSelected,
    removeTab,
    resetTabs,
    setActiveKey,
    setHomeFormValues,
    setKeyTabToDelete,
    setPlanTypeSelected,
    setShowModal,
    showModal,
    stepTwoForm,
    tabs,
  } = useContext(StepTwoFormContext);
  const { resetAllFormFields } = useContext(StepThreeFormContext)
  const { handleTabErrors, quotationForm } = useContext(WizardDataFormContext);
  const { setCanShowSpinner, setPlansSelectedByTabs, setPlansSelected, setUserCanDeleteTabs } = useContext(GenericDataFormContext);
  const { setCurrentStep, quotationInfo, loadSaveQuotationState, renewalQuotationState } = useContext(QuotationInfoContext);

  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [unconfirmedPlanType, setUnconfirmedPlanType] = useState<
    PLAN_TYPE.OPEN | PLAN_TYPE.CLOSED | null
  >(null);

  const validateFeatures = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      let hasError = false;

      try {
        for (const { key } of tabs) {
          const featureList: FeatureRisk[] = [];

          const featuresFiltered = allFeatures
            .filter((feature) => String(feature.keyTab) === String(key))
            .map(({ features }) => features);

          featuresFiltered.forEach((featureGroup) => {
            featureGroup.forEach(({ codigo }) => {
              const fieldName = `feature-tab-${key}-${codigo}`;
              const fieldValue = stepTwoForm?.getFieldValue(fieldName);
              const alreadyExists = featureList.some(
                (feature) => String(feature.codigo) === String(codigo)
              );
              if (!alreadyExists) {
                featureList.push({
                  codigo,
                  valor: fieldValue,
                });
              }
            });
          });

          const params = {
            numeroCotizacion: Number(quotationInfo?.token),
            rama: Number(quotationInfo?.branch),
            componente: key,
            codigoPostal: Number(quotationForm?.cliente?.codigoPostal),
            sufijoPostal: Number(quotationForm?.cliente?.sufijoCodigoPostal),
            caracteristicas: featureList,
          };

          if (!hasError) {
            try {
              await QuotationService.postValidateFeaturesRiskVarious(params);
            } catch (error) {
              hasError = true;
              reject(new Error('An error occurred during validation'));
              return;
            }
          }
        }

        resolve('');
      } catch (error) {
        reject(new Error(''));
      }
    });
  }, [
    tabs,
    allFeatures,
    quotationInfo,
    quotationForm,
    stepTwoForm,
  ]);

  const onValidateForm = async () => {
    try {
      setCanShowSpinner(true);
      await stepTwoForm?.validateFields();

      setHomeFormValues(stepTwoForm?.getFieldsValue());

      if(planTypeSelected === PLAN_TYPE.OPEN) {
        await validateFeatures();
        setPlansSelectedByTabs(tabs, stepTwoForm);
      }
      setUserCanDeleteTabs(false);
      setCurrentStep(QuotationStep.THREE);
    } catch (error: any) {
      if (error && error?.errorFields?.length > 0) {
        handleTabErrors(error?.errorFields, ['tab'], 'Hogar');
      }
    } finally {
      setCanShowSpinner(false);
    }
  };

  const handlerBackButton = () => {
    setHomeFormValues(stepTwoForm?.getFieldsValue());
    setCurrentStep(QuotationStep.ONE);
  };

  const onEdit = (targetKey: any, action: 'add' | 'remove') => {
    if (action === 'add') {
      addNewTab();

      return;
    }

    setShowModal(true);
  };

  const handleOk = () => {
    if (keyTabToDelete !== null) {
      removeTab(keyTabToDelete);
      setKeyTabToDelete(null);
    }

    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
    setKeyTabToDelete(null);
  };

  const handleChangePlanType = (e: any) => {
    setUnconfirmedPlanType(e.target.value);
    setShowConfirmationModal(true);
  };

  const handleCancelConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleConfirmActionModal = useCallback(() => {
    if (unconfirmedPlanType && unconfirmedPlanType !== planTypeSelected) {
      setPlanTypeSelected(unconfirmedPlanType);

      setHomeFormValues(null);

      resetTabs(unconfirmedPlanType);

      setPlansSelected([]);

      resetAllFormFields();
    };

    setUnconfirmedPlanType(null);
    setShowConfirmationModal(false);
  }, [
    setPlanTypeSelected,
    unconfirmedPlanType,
    planTypeSelected,
    setHomeFormValues,
    resetTabs,
    setPlansSelected,
    setUnconfirmedPlanType,
    setShowConfirmationModal,
    resetAllFormFields
  ]);

  const handlePressTab = (newActiveKey: string) => {
    setActiveKey(newActiveKey);
  };

  useEffect(() => {
    const isRenewal = !!renewalQuotationState;
    const shouldLoadValues = isRenewal
      ? renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
      : !loadSaveQuotationState;

    if (shouldLoadValues && homeFormValues && Object.keys(homeFormValues).length > 0) {
      setTimeout(() => {
        stepTwoForm?.setFieldsValue(homeFormValues);
      }, 100);
    }
  }, [loadSaveQuotationState, renewalQuotationState, homeFormValues, stepTwoForm]);

  return (
    <div className='div-property-data-step-two-home'>
      <Form
        formProps={{
          form: stepTwoForm,
          name: 'property-data-home-form',
        }}
      >
        <ContentSection level='h3' title='Tipo de Plan'>
          <RowSize>
            <ColSize size={100}>
              <FormAntd.Item label='Tipo de Plan: '>
                <Radio.Group
                  value={planTypeSelected}
                  onChange={(e) => handleChangePlanType(e)}
                >
                  <Radio value={PLAN_TYPE.OPEN} disabled={isPlanTypeADisabled}>
                    Abierto
                  </Radio>
                  <Radio value={PLAN_TYPE.CLOSED} disabled={isPlanTypeCDisabled}>
                    Cerrado
                  </Radio>
                </Radio.Group>
              </FormAntd.Item>
            </ColSize>
          </RowSize>
        </ContentSection>
        {tabs.length ? (
          <Tabs
            type='editable-card'
            activeKey={activeKey}
            onEdit={onEdit}
            hideAdd={!areTabActionsAllowed}
            items={tabs}
            handlePressTab={handlePressTab}
          />
        ) : (
          <></>
        )}
      </Form>
      <QuotationStepButtons
        label='Continuar'
        onClick={onValidateForm}
        onPressBackButton={handlerBackButton}
      />

      <ModalDeleteTab
        open={showModal}
        onCancel={handleCancel}
        onOk={handleOk}
      />

      <ConfirmationModal
        open={showConfirmationModal}
        onCancel={handleCancelConfirmationModal}
        onConfirm={handleConfirmActionModal}
        confirmButtonLabel='Confirmar'
        title='¿Desea cambiar el Tipo de Plan?'
        description='Si cambia el Tipo de Plan, los datos cargados de esta operación serán eliminados y tendrá que volver a cargar de vuelta todos los datos de la misma. ¿Desea continuar?'
      />
    </div>
  );
};
